import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { SignIn, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div
        className="hidden lg:flex relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, rgba(212, 255, 58, 0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(212, 255, 58, 0.12), transparent 50%)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src="/adxsk-logo-dark.png" alt="Akshat Dani x SK" style={{ height: 44, width: "auto" }} />
          </div>
          <div className="space-y-6 max-w-lg">
            <p className="label-uppercase" style={{ color: "var(--lime)" }}>// Consulting Hub</p>
            <h1 className="font-display text-5xl font-black leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
              Tailored AI<br />
              <span style={{ color: "var(--lime)" }}>consultations</span>,<br />
              for every conversation.
            </h1>
            <p className="text-lg opacity-70 leading-relaxed">
              A curated lineup of consulting GPTs by Akshat Dani — frameworks, decisions, and pitches at your fingertips.
            </p>
          </div>
          <div className="opacity-50 text-xs font-mono uppercase tracking-widest">© Akshat Dani</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-8">
            <button data-testid="login-theme-toggle" className="btn btn-ghost btn-sm" onClick={toggle}>
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/adxsk-logo.png" alt="Akshat Dani x SK" className="modern-brand-logo modern-brand-logo-light" style={{ height: 36 }} />
            <img src="/adxsk-logo-dark.png" alt="Akshat Dani x SK" className="modern-brand-logo modern-brand-logo-dark" style={{ height: 36 }} />
          </div>

          <p className="label-uppercase mb-3" style={{ color: "var(--accent)" }}>Welcome Back</p>
          <h2 className="font-display text-4xl font-bold mb-2">Sign in to your hub</h2>
          <p className="mb-8" style={{ color: "var(--muted)" }}>
            Enter your credentials to continue your consultations.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-uppercase block mb-2" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-uppercase block mb-2" htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm py-2 px-3 rounded-md"
                style={{ background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.3)" }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              <SignIn size={18} weight="duotone" />
              {loading ? "Signing in..." : "Sign in"}
            </button>
            <div className="text-right -mt-2">
              <button
                type="button"
                className="text-xs font-semibold"
                style={{ color: "var(--accent)" }}
                onClick={() => setForgotOpen(true)}
                data-testid="login-forgot-password"
              >
                Forgot password?
              </button>
            </div>
          </form>

          <div className="mt-6 text-sm text-center" style={{ color: "var(--muted)" }}>
            Don&apos;t have an account?{" "}
            <Link to="/register" data-testid="login-to-register-link" className="font-semibold">
              Create one
            </Link>
          </div>
        </div>
      </div>
      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} initialEmail={email} />}
    </div>
  );
}

function ForgotPasswordModal({ onClose, initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), note: note.trim() || undefined });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tnc-modal-backdrop" role="dialog" aria-modal="true" data-testid="forgot-password-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tnc-modal-card" style={{ position: "relative", maxWidth: 480 }}>
        <button type="button" className="tnc-modal-close" onClick={onClose} aria-label="Close" data-testid="forgot-password-close">
          <X size={16} weight="bold" />
        </button>
        <header className="tnc-modal-head">
          <div style={{ flex: 1 }}>
            <p className="tnc-modal-eyebrow">Forgot password</p>
            <h2 className="tnc-modal-title" id="forgot-pw-title">
              Request a <em>password reset</em>
            </h2>
            <p className="tnc-modal-sub">
              Your request goes straight to the admin. They&rsquo;ll create a temporary password and share it with you on WhatsApp so you can sign in and choose a new one.
            </p>
          </div>
        </header>
        {sent ? (
          <div className="tnc-modal-body">
            <p style={{ fontSize: "0.92rem", lineHeight: 1.6 }} data-testid="forgot-password-success">
              ✓ Request sent. If an account with that email exists, the admin will reach out to you shortly on WhatsApp with a temporary password.
            </p>
            <div className="tnc-modal-foot" style={{ borderTop: 0, padding: 0, marginTop: "1rem" }}>
              <button type="button" className="tnc-modal-agree" onClick={onClose} data-testid="forgot-password-done">
                Got it
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="tnc-modal-body" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                Your email
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="textarea kb-textarea"
                  style={{ marginTop: "0.35rem" }}
                  placeholder="you@example.com"
                  data-testid="forgot-password-email"
                />
              </label>
              <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                Note for the admin (optional)
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="textarea kb-textarea"
                  style={{ marginTop: "0.35rem" }}
                  placeholder="Anything the admin should know"
                  data-testid="forgot-password-note"
                />
              </label>
              {error && <p className="tnc-modal-error">{error}</p>}
            </div>
            <footer className="tnc-modal-foot">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="tnc-modal-agree" disabled={submitting} data-testid="forgot-password-submit">
                {submitting ? "Sending…" : "Send request"}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
}
